import type { IGameState, IMoveResult, TMoveError, TPlayer } from './types';
import { createEmptyBoard } from './utils/create-board';
import { checkWinner } from './utils/check-winner';
import { checkDraw } from './utils/check-draw';
import { getDropRow } from './utils/get-valid-columns';
import { COLS } from './constants';

export class FourInARowGame {
  private state: IGameState;

  constructor() {
    this.state = {
      board: createEmptyBoard(),
      currentPlayer: 0,
      status: 'idle',
      winner: null,
      winningLine: null,
    };
  }

  start(): void {
    this.state.status = 'playing';
  }

  makeMove(player: TPlayer, col: number): IMoveResult {
    if (this.state.status !== 'playing') {
      return this.createMoveResult(false, 'not_playing', null, null);
    }

    if (player !== this.state.currentPlayer) {
      return this.createMoveResult(false, 'not_your_turn', null, null);
    }

    if (col < 0 || col >= COLS) {
      return this.createMoveResult(false, 'invalid_column', null, null);
    }

    const row = getDropRow(this.state.board, col);
    if (row < 0) {
      return this.createMoveResult(false, 'column_full', null, null);
    }

    this.state.board[row][col] = player;

    const winnerCheck = checkWinner(this.state.board);
    if (winnerCheck.hasWinner) {
      this.state.status = 'finished';
      this.state.winner = winnerCheck.winner;
      this.state.winningLine = winnerCheck.winningLine;
      return this.createMoveResult(
        true,
        null,
        row,
        col,
        true,
        winnerCheck.winner ?? null,
      );
    }

    if (checkDraw(this.state.board)) {
      this.state.status = 'draw';
      return this.createMoveResult(true, null, row, col, true, null, true);
    }

    this.switchPlayer();
    return this.createMoveResult(true, null, row, col);
  }

  getState(): IGameState {
    return {
      ...this.state,
      board: this.state.board.map((r) => [...r]),
    };
  }

  reset(): void {
    this.state = {
      board: createEmptyBoard(),
      currentPlayer: 0,
      status: 'idle',
      winner: null,
      winningLine: null,
    };
  }

  private switchPlayer(): void {
    this.state.currentPlayer = this.state.currentPlayer === 0 ? 1 : 0;
  }

  private createMoveResult(
    success: boolean,
    error: TMoveError = null,
    row: number | null = null,
    col: number | null = null,
    isGameOver: boolean = false,
    winner: TPlayer | null = null,
    isDraw: boolean = false,
  ): IMoveResult {
    return {
      success,
      error,
      row,
      col,
      gameState: this.getState(),
      isGameOver,
      winner,
      isDraw,
    };
  }
}
